import React, {
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Button from '../../components/Button';
import { getSpace } from '../api/spaces/[spaceId]';
import {
  currencyFormat,
  getCloseTime,
  getOpenTime,
  spaceOpen,
  getAvailableDates,
} from '../../utils';
import { getSpaceEvents } from '../api/spaces/[spaceId]/events';
import BookingModal from '../../components/BookingModal';
import Footer from '../../components/Footer';
import classNames from 'classnames';
import Head from 'next/head';
import { getSpaceJsonLd } from '../../utils/seo/schema.org';
import { GetServerSidePropsContext } from 'next';
import ImagesCarousel from '../../components/ImagesCarousel';
import { FiMapPin } from 'react-icons/fi';
import BreadCrumbs from '../../components/BreadCrumbs';
import { NextSeo } from 'next-seo';
import ProductCard from '@/components/ProductCard';
import WingsCard from '@/components/WingsCard';
import ServiceItem from '@/components/ServiceItem';
import NewCarousel from '@/components/NewCarousel';
import { useRouter } from 'next/router';
import BookingSection from '@/components/BookingSection';
import GoogleMaps from '@/components/StaticMapComponent';
import Link from 'next/link';
import useIsMobile from '@/hooks/useIsMobile';

type SpaceDetailProps = {
  space: SpaceType;
  events: EventType[];
  cityId: string;
};

const SpaceDetail = ({
  space,
  events,
  cityId,
}: SpaceDetailProps): ReactElement => {
  const availableDates = getAvailableDates(events);
  const [showingBooking, setShowingBooking] = useState(false);
  const [showingDescription, setShowingDescription] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [shrink, setShrink] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [similarSpaces, setSimilarSpaces] = useState<SpaceType[]>([]);
  const router = useRouter();
  const language = router.locale as SupportedLanguages;
  const isSpanish = language === 'es';
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchSimilar = async () => {
      setLoadingSimilar(true);
      try {
        const response = await fetch(`/api/${space.promoter_id}/spaces`);
        const similarSpaces = await response.json();
        setSimilarSpaces(
          similarSpaces.results.filter((s: SpaceType) => s.id !== space.id)
        );
      } catch (err) {
        console.log(err);
      }
      setLoadingSimilar(false);
    };
    fetchSimilar();
  }, [space.promoter_id, space.id]);

  const toggleBooking = () => {
    setShowingBooking(!showingBooking);
  };

  const bookingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (bookingRef.current && !bookingRef.current.contains(event.target)) {
        setShowingBooking(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [bookingRef]);

  useEffect(() => {
    if (window.innerWidth > 768) {
      setShrink(false);
    }
  }, []);

  // Function to handle scrolling
  const handleScroll = useCallback(() => {
    if (window.innerWidth > 768) {
      return;
    }

    if (window.scrollY >= lastScrollY && window.scrollY > 80) {
      setShrink(false); // If scrolling up, show the navigator
    } else {
      setShrink(true); // If scrolling down, hide the navigator
    }
    setLastScrollY(window.scrollY);
  }, [lastScrollY]);

  // Set up the scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, lastScrollY]);

  return (
    <>
      <NextSeo
        title={`Tikipal - ${space.name} - ${cityId}`}
        description={`${cityId} - ${space.name} - ${space.description}`}
        canonical={`https://tikipal.com.co/${cityId}/${space.slug}`}
        languageAlternates={[
          {
            hrefLang: 'es',
            href: `https://tikipal.com.co/${cityId}/${space.slug}`,
          },
        ]}
        openGraph={{
          url: `https://tikipal.com.co/${cityId}/${space.slug}`,
          title: `Tikipal - ${space.name}`,
          description: `${space.name} - ${space.description}`,
          type: 'website',
          images: [
            {
              url: space.profile_image,
              width: 200,
              height: 200,
              alt: 'Tikipal',
            },
          ],
          site_name: 'Tikipal',
        }}
      />
      <div
        className={classNames('h-full', {
          'overflow-hidden': showingBooking,
        })}
      >
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={getSpaceJsonLd(space, cityId)}
            key="product-jsonld"
          />
        </Head>
        <BreadCrumbs currentLabel={space.name} />
        <div>
          <div className="w-full flex justify-center">
            <div className="relative flex flex-col items-center h-[16rem]  w-full">
              <ImagesCarousel
                images={[
                  {
                    url: space.profile_image,
                    alt: `${space.name}-cover`,
                  },
                ].concat(
                  space.space_images.map((i) => ({
                    url: i.image,
                    alt: `${space.name}-${i.id}`,
                  }))
                )}
              />
            </div>
          </div>
          <div className="w-full justify-center grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Space Details */}
            <div className="w-full max-w-7xl px-6 flex flex-col gap-4 col-span-1 md:col-span-3">
              <div className="w-full flex flex-col items-center mb-2">
                <h1 className="text-copper text-2xl z-10 w-full text-left mb-0">
                  {space.name.split(' ').map((w, i) => {
                    if (i === 0) return <strong key={i}>{w} </strong>;
                    return (
                      <span key={i} className="capitalize">
                        {w}{' '}
                      </span>
                    );
                  })}
                </h1>
                {space.google_maps_url ? (
                  <Link
                    href={space.google_maps_url}
                    target={isMobile ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="cursor-pointer underline z-10 w-full flex text-sm"
                  >
                    <p className="text-base m-0 flex gap-2 items-center w-full">
                      <FiMapPin className="flex-shrink-0" />
                      <span className="line-clamp-2">
                        {space.street_address}
                      </span>
                    </p>
                  </Link>
                ) : (
                  <p className="text-base m-0 flex gap-2 items-center w-full">
                    <FiMapPin className="flex-shrink-0" />
                    <span className="line-clamp-2">{space.street_address}</span>
                  </p>
                )}
                <div className="z-10 w-full flex text-sm">
                  {space.music
                    .slice(0, 4)
                    .map((m, i, arr) =>
                      i + 1 < arr.length ? `${m.name} · ` : m.name
                    )}
                </div>
              </div>
              <div className=" pb-2">
                <WingsCard
                  first={
                    spaceOpen(space.space_schedules) ? 'Abierto' : 'Cerrado'
                  }
                  second={`${
                    space?.space_schedules?.length &&
                    getOpenTime(space.space_schedules)
                  }
                   - 
                  ${
                    space?.space_schedules?.length &&
                    getCloseTime(space.space_schedules)
                  }`}
                  third={space.capacity.toString()}
                  fourth="Aforo"
                />
              </div>
              {showingDescription ? (
                <>
                  <div className="text-sm">{space.description}</div>
                  <hr className="border-gray-300" />
                  <h2 className="font-bold text-base">
                    {isSpanish ? 'Nuestro Estilo' : 'Our Style'}
                  </h2>
                  <div className="text-sm">{space.product_description}</div>
                </>
              ) : (
                <div className="text-sm">
                  {space?.description?.slice(0, 100)}...
                </div>
              )}
              <a
                className="text-copper cursor-pointer underline block"
                onClick={() => setShowingDescription(!showingDescription)}
              >
                {showingDescription
                  ? isSpanish
                    ? 'Mostrar menos'
                    : 'Show less'
                  : isSpanish
                  ? 'Mostrar más'
                  : 'Show more'}
              </a>
              <hr className="border-gray-300" />
              {!!space.space_products.length && (
                <>
                  <h2 className="font-bold text-xl ">
                    {isSpanish
                      ? 'Lo que vas a encontrar'
                      : 'What you will find'}
                  </h2>
                  <div className="flex gap-2 overflow-scroll pb-4 items-center">
                    {space.space_products.map((p) => (
                      <ProductCard key={p.id} product={p} />
                    ))}
                  </div>
                </>
              )}
              {!!space.services.length && (
                <>
                  <h2 className="font-bold text-xl ">
                    {isSpanish
                      ? 'Lo que este lugar ofrece'
                      : 'What this place offers'}
                  </h2>
                  <div className="flex flex-col overflow-scroll pb-4 items-start">
                    {space.services.map((s) => (
                      <ServiceItem key={s.id} name={s.name} />
                    ))}
                  </div>
                </>
              )}
              <div className="w-full flex flex-col gap-2 px-0 mb-4">
                <h2 className="font-bold text-xl">
                  {isSpanish ? 'Ubicación' : 'Location'}
                </h2>
                <p className="text-base m-0 flex gap-2 items-center w-full">
                  <FiMapPin className="flex-shrink-0" />
                  <span className="line-clamp-2">{space.street_address}</span>
                </p>
                <div className="w-full">
                  {space.google_maps_url && (
                    <Link
                      href={space.google_maps_url}
                      target={isMobile ? '_self' : '_blank'}
                      rel="noopener noreferrer"
                      className="overflow-hidden rounded-lg relative"
                    >
                      <GoogleMaps
                        lat={parseFloat(space.latitude as string)}
                        lng={parseFloat(space.longitude as string)}
                        address={space.street_address as string}
                        name={space.name}
                        googleMapsUrl={space.google_maps_url}
                        isSpanish={isSpanish}
                      />
                    </Link>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4 col-span-1 md:col-span-2">
              <BookingSection space={space} events={events} />
            </div>
          </div>
        </div>
        {!loadingSimilar &&
          similarSpaces.length > 0 &&
          space.slug !== 'mr-col-leather' &&
          space.slug !== 'mitos' && (
            <div>
              <h2 className="font-bold text-xl pb-4 px-6 mt-4">
                {isSpanish ? 'Otros sitios similares' : 'Other similar places'}
              </h2>

              <NewCarousel spaces={similarSpaces} />
            </div>
          )}
        {showingBooking ? (
          <BookingModal space={space} events={events} onClose={toggleBooking} />
        ) : (
          <div className="w-full flex justify-center">
            <div
              className={classNames(
                'flex justify-between items-start bg-white p-4 fixed w-screen border-t border-gray-100 z-50 white-shadow max-w-7xl px-6 bottom-0 transition-all duration-300 md:hidden',
                {
                  'pb-20': shrink,
                  'pb-4': !shrink,
                }
              )}
            >
              <div className="flex flex-col">
                <div className="font-bold text-copper glow-copper">
                  {(space.cover_price as number) > 0
                    ? currencyFormat(space.cover_price as number)
                    : isSpanish
                    ? 'GRATIS'
                    : 'FREE'}
                </div>
                {(space.cover_price as number) > 0 && (
                  <div className="text-xs">
                    {isSpanish ? 'Por entrada' : 'Per ticket'}
                  </div>
                )}
              </div>
              <div className="w-44">
                {availableDates.length > 0 ? (
                  <Button
                    onClick={toggleBooking}
                    disabled={!availableDates.length}
                    id={`${cityId}/${space.slug}-buy-ticket-init`}
                  >
                    {(space.cover_price as number) > 0
                      ? isSpanish
                        ? 'Comprar Entrada'
                        : 'Buy Ticket'
                      : isSpanish
                      ? 'Obtener Entrada'
                      : 'Get Ticket'}
                  </Button>
                ) : (
                  <p className="text-sm text-gray-500">
                    No hay eventos disponibles
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    ctx.res.setHeader(
      'Cache-Control',
      'public, s-maxage=10, stale-while-revalidate=59'
    );

    const { spaceId, cityId } = ctx.query;
    const space = await getSpace(spaceId as string);
    const eventsData = await getSpaceEvents(space.id);

    return {
      props: {
        space,
        cityId,
        events: eventsData.results as Array<any>,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      notFound: true,
    };
  }
}

export default SpaceDetail;
